import { z } from "zod/v4";

import { z_JsonValue } from "./chat-protocol";
import { z_ChartData, z_ChartPresentationKind } from "./chart";
import { z_FeedCursor, z_FeedItemResponse } from "./feed/feed-item.types";

export const CHAT_V8_FEED_WIDGET_MODES = ["personalized"] as const;
export const z_ChatV8FeedWidgetMode = z.enum(CHAT_V8_FEED_WIDGET_MODES);

export const z_ChatV8FeedWidgetDescriptor = z.object({
  kind: z.literal("feed_widget").default("feed_widget"),
  limit: z.number().int().min(1).max(10).default(5),
  mode: z_ChatV8FeedWidgetMode.default("personalized"),
  title: z.string().min(1).max(160).optional(),
});
export type T_ChatV8FeedWidgetDescriptor = z.infer<
  typeof z_ChatV8FeedWidgetDescriptor
>;

export const z_ChatV8FeedWidgetData = z.object({
  descriptor: z_ChatV8FeedWidgetDescriptor,
  emptyReason: z.string().optional(),
  generatedAt: z.string(),
  items: z.array(z_FeedItemResponse),
  nextCursor: z_FeedCursor.nullable(),
});
export type T_ChatV8FeedWidgetData = z.infer<typeof z_ChatV8FeedWidgetData>;

export const z_ChatV8MessagePartKind = z.enum(["markdown", "widget"]);
export type T_ChatV8MessagePartKind = z.infer<typeof z_ChatV8MessagePartKind>;

export const z_ChatV8MessagePartRole = z.enum(["thinking", "final"]);
export type T_ChatV8MessagePartRole = z.infer<typeof z_ChatV8MessagePartRole>;

export const CHAT_V8_STOCK_WIDGET_INTERVALS = [
  "1D",
  "1W",
  "1M",
  "6M",
  "1Y",
  "5Y",
  "MAX",
] as const;

export const z_ChatV8StockWidgetInterval = z.enum(
  CHAT_V8_STOCK_WIDGET_INTERVALS,
);
export type T_ChatV8StockWidgetInterval = z.infer<
  typeof z_ChatV8StockWidgetInterval
>;

export const CHAT_V8_INSIGHT_KINDS = [
  "earnings_intelligence",
  "insider_conviction",
  "peer_divergence",
  "thesis_evidence",
  "portfolio_fit",
] as const;

// Top-level render contracts describe visual structures, not research topics.
// Topic-specific insight behavior belongs to CHAT_V8_INSIGHT_KINDS inside the
// single generalized insight descriptor and snapshot.
export const CHAT_V8_WIDGET_TYPES = [
  "stock",
  "table",
  "chart",
  "feed_widget",
  "insight",
] as const;

export const z_ChatV8WidgetType = z.enum(CHAT_V8_WIDGET_TYPES);
export type T_ChatV8WidgetType = z.infer<typeof z_ChatV8WidgetType>;

// Canonical ticker schema for every Chat V8 widget descriptor and snapshot.
export const z_ChatV8TickerSymbol = z
  .string()
  .trim()
  .min(1)
  .max(24)
  .transform((value) => value.toUpperCase());
export type T_ChatV8TickerSymbol = z.infer<typeof z_ChatV8TickerSymbol>;

export const z_ChatV8ChartRange = z.enum(["1M", "6M", "1Y", "5Y"]);
export type T_ChatV8ChartRange = z.infer<typeof z_ChatV8ChartRange>;

const z_ChatV8PriceChartWidgetDescriptorBase = z.object({
  chartKind: z.enum(["price", "price_comparison"]),
  symbols: z.array(z_ChatV8TickerSymbol).min(1).max(5),
  range: z_ChatV8ChartRange,
  title: z.string().max(120).optional(),
  normalize: z.boolean().optional(),
});

type T_ChatV8PriceChartWidgetDescriptorBase = z.infer<
  typeof z_ChatV8PriceChartWidgetDescriptorBase
>;

function normalizePriceChartDescriptor<
  T_Descriptor extends T_ChatV8PriceChartWidgetDescriptorBase,
>(descriptor: T_Descriptor) {
  return {
    ...descriptor,
    normalize:
      descriptor.normalize ?? descriptor.chartKind === "price_comparison",
  };
}

export const z_ChatV8PriceChartWidgetDescriptor =
  z_ChatV8PriceChartWidgetDescriptorBase.transform(
    normalizePriceChartDescriptor,
  );
export type T_ChatV8PriceChartWidgetDescriptor = z.infer<
  typeof z_ChatV8PriceChartWidgetDescriptor
>;

// Backend-resolved chart presentations add only an optional presentation
// discriminator. The price-shaped compatibility fields remain parseable by
// shipped clients; numeric series still come only from the backend artifact
// factory and are never accepted from deterministic orchestration or models.
export const z_ChatV8ChartWidgetDescriptor =
  z_ChatV8PriceChartWidgetDescriptorBase
    .extend({ presentationKind: z_ChartPresentationKind.optional() })
    .transform(normalizePriceChartDescriptor);
export type T_ChatV8ChartWidgetDescriptor = z.infer<
  typeof z_ChatV8ChartWidgetDescriptor
>;

// RAI-1079: internal data-vendor names must never reach user-facing chart
// captions. Persisted snapshots are immutable, so historical rows that
// mention vendors are scrubbed here at parse time — the single schema both
// the backend artifact factory and every client renderer go through.
export function scrubVendorMentions(text: string): string {
  return text
    .replace(/\bFMP earnings\b/gi, "Earnings reports")
    .replace(/\bFMP ratios TTM\b/gi, "Trailing-twelve-month ratios")
    .replace(/\bFMP insider trading\b/gi, "Insider filings")
    .replace(/\bFMP estimates\b/gi, "Analyst estimates")
    .replace(/\s*(?:from|via)\s+FMP\b/gi, "")
    .replace(/\bFMP\s*/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export const z_ChatV8ChartWidgetSnapshotData = z.object({
  chart: z_ChartData,
  asOf: z.string(),
  sourceNote: z.string().transform(scrubVendorMentions),
});
export type T_ChatV8ChartWidgetSnapshotData = z.infer<
  typeof z_ChatV8ChartWidgetSnapshotData
>;

export const z_ChatV8StockWidget = z.object({
  symbol: z_ChatV8TickerSymbol,
});
export type T_ChatV8StockWidget = z.infer<typeof z_ChatV8StockWidget>;

export const z_ChatV8StockWidgetBar = z.object({
  t: z.string(),
  o: z.number(),
  h: z.number(),
  l: z.number(),
  c: z.number(),
  v: z.number(),
});
export type T_ChatV8StockWidgetBar = z.infer<typeof z_ChatV8StockWidgetBar>;

export const z_ChatV8StockWidgetIntervalStats = z.object({
  firstClose: z.number().nullable(),
  high: z.number().nullable(),
  lastClose: z.number().nullable(),
  low: z.number().nullable(),
  renderCount: z.number().int().min(0),
  sourceCount: z.number().int().min(0),
});
export type T_ChatV8StockWidgetIntervalStats = z.infer<
  typeof z_ChatV8StockWidgetIntervalStats
>;

export const z_ChatV8StockWidgetColumnarInterval = z.object({
  c: z.array(z.number()),
  encoding: z.literal("columnar_close_v1"),
  stats: z_ChatV8StockWidgetIntervalStats,
  t: z.array(z.number().int()),
  tUnit: z.literal("epoch_ms"),
});
export type T_ChatV8StockWidgetColumnarInterval = z.infer<
  typeof z_ChatV8StockWidgetColumnarInterval
>;

export const z_ChatV8StockWidgetData = z.object({
  symbol: z.string(),
  name: z.string().nullable(),
  exchange: z.string().nullable(),
  image: z.string().nullable(),
  isCrypto: z.boolean(),
  isEtf: z.boolean(),
  latestPrice: z.number().nullable(),
  changeAmount: z.number().nullable(),
  changePercent: z.number().nullable(),
  intervals: z.object({
    "1D": z.array(z_ChatV8StockWidgetBar),
    "1W": z.array(z_ChatV8StockWidgetBar),
    "1M": z.array(z_ChatV8StockWidgetBar),
    "6M": z.array(z_ChatV8StockWidgetBar),
    "1Y": z.array(z_ChatV8StockWidgetBar),
    "5Y": z.array(z_ChatV8StockWidgetBar),
    MAX: z.array(z_ChatV8StockWidgetBar),
  }),
  metrics: z.object({
    beta: z.number().nullable(),
    dividendYield: z.number().nullable(),
    marketCap: z.number().nullable(),
    peRatio: z.number().nullable(),
  }),
});
export type T_ChatV8StockWidgetData = z.infer<typeof z_ChatV8StockWidgetData>;

export const z_ChatV8StockWidgetDataResponse = z.object({
  data: z_ChatV8StockWidgetData.nullable(),
  error: z.literal("NOT_FOUND").nullable(),
  warnings: z.array(z.string()).optional(),
});
export type T_ChatV8StockWidgetDataResponse = z.infer<
  typeof z_ChatV8StockWidgetDataResponse
>;

export const z_ChatV8StockWidgetColumnarData = z.object({
  symbol: z.string(),
  name: z.string().nullable(),
  exchange: z.string().nullable(),
  image: z.string().nullable(),
  isCrypto: z.boolean(),
  isEtf: z.boolean(),
  latestPrice: z.number().nullable(),
  changeAmount: z.number().nullable(),
  changePercent: z.number().nullable(),
  intervals: z.object({
    "1D": z_ChatV8StockWidgetColumnarInterval,
    "1W": z_ChatV8StockWidgetColumnarInterval,
    "1M": z_ChatV8StockWidgetColumnarInterval,
    "6M": z_ChatV8StockWidgetColumnarInterval,
    "1Y": z_ChatV8StockWidgetColumnarInterval,
    "5Y": z_ChatV8StockWidgetColumnarInterval,
    MAX: z_ChatV8StockWidgetColumnarInterval,
  }),
  metrics: z.object({
    beta: z.number().nullable(),
    dividendYield: z.number().nullable(),
    marketCap: z.number().nullable(),
    peRatio: z.number().nullable(),
  }),
  snapshotEncoding: z.literal("stock_columnar_close_v1"),
});
export type T_ChatV8StockWidgetColumnarData = z.infer<
  typeof z_ChatV8StockWidgetColumnarData
>;

export const z_ChatV8StockWidgetColumnarDataResponse = z.object({
  data: z_ChatV8StockWidgetColumnarData.nullable(),
  error: z.literal("NOT_FOUND").nullable(),
  warnings: z.array(z.string()).optional(),
});
export type T_ChatV8StockWidgetColumnarDataResponse = z.infer<
  typeof z_ChatV8StockWidgetColumnarDataResponse
>;

export const z_ChatV8InsightKind = z.enum(CHAT_V8_INSIGHT_KINDS);
export type T_ChatV8InsightKind = z.infer<typeof z_ChatV8InsightKind>;

export const z_ChatV8InsightWidgetDescriptor = z.object({
  insightKind: z_ChatV8InsightKind,
  symbol: z_ChatV8TickerSymbol,
  title: z.string().min(1).max(160).optional(),
  peerSymbols: z.array(z_ChatV8TickerSymbol).max(12).optional(),
  window: z.string().min(1).max(64).optional(),
  fiscalPeriod: z.string().min(1).max(64).optional(),
  thesis: z.string().min(1).max(2_000).optional(),
  // Historical company-analysis artifacts may carry narrative presentation
  // intent (RAI-1080). Current authoring rejects it because title + prose is
  // not a widget (RAI-1100); the field remains solely for old thread parsing.
  presentationKind: z.enum(["card", "narrative"]).optional(),
  portfolioScenario: z
    .object({
      amountUsd: z.number().finite().positive().optional(),
    })
    .optional(),
});
export type T_ChatV8InsightWidgetDescriptor = z.infer<
  typeof z_ChatV8InsightWidgetDescriptor
>;

export const z_ChatV8InsightWidgetTone = z.enum([
  "positive",
  "negative",
  "neutral",
  "warning",
]);
export type T_ChatV8InsightWidgetTone = z.infer<
  typeof z_ChatV8InsightWidgetTone
>;

export const z_ChatV8InsightWidgetDataQuality = z.enum([
  "complete",
  "partial",
  "unavailable",
]);
export type T_ChatV8InsightWidgetDataQuality = z.infer<
  typeof z_ChatV8InsightWidgetDataQuality
>;

export const z_ChatV8InsightWidgetMetric = z.object({
  label: z.string(),
  value: z_JsonValue,
  displayValue: z.string(),
  tone: z_ChatV8InsightWidgetTone.optional(),
});
export type T_ChatV8InsightWidgetMetric = z.infer<
  typeof z_ChatV8InsightWidgetMetric
>;

export const z_ChatV8InsightWidgetRow = z.object({
  label: z.string(),
  value: z.string().optional(),
  detail: z.string().optional(),
  tone: z_ChatV8InsightWidgetTone.optional(),
  sourceLabel: z.string().transform(scrubVendorMentions).optional(),
});
export type T_ChatV8InsightWidgetRow = z.infer<typeof z_ChatV8InsightWidgetRow>;

// Canonical five-step analysis grade. Declared at the generalized insight
// section boundary because company scorecards now render through the current
// insight artifact contract rather than a retired composite widget
// (RAI-1081). The same schema remains the source of truth for backend
// company-analysis computation below.
export const z_ChatV8AnalysisGrade = z.enum([
  "excellent",
  "good",
  "medium",
  "poor",
  "very_poor",
]);
export type T_ChatV8AnalysisGrade = z.infer<typeof z_ChatV8AnalysisGrade>;

export const z_ChatV8InsightWidgetSection = z.object({
  title: z.string(),
  rows: z.array(z_ChatV8InsightWidgetRow),
  // Optional graded-section presentation. Existing insight snapshots remain
  // unchanged; prepared company-analysis artifacts opt into this grammar.
  body: z.string().min(1).optional(),
  grade: z_ChatV8AnalysisGrade.optional(),
});
export type T_ChatV8InsightWidgetSection = z.infer<
  typeof z_ChatV8InsightWidgetSection
>;

export const z_ChatV8InsightWidgetPeer = z.object({
  symbol: z.string(),
  name: z.string().optional(),
  metrics: z.array(z_ChatV8InsightWidgetMetric),
});
export type T_ChatV8InsightWidgetPeer = z.infer<
  typeof z_ChatV8InsightWidgetPeer
>;

export const z_ChatV8InsightWidgetVisualBase = z.object({
  dataQuality: z_ChatV8InsightWidgetDataQuality,
  subtitle: z.string().optional(),
  title: z.string(),
});

export const z_ChatV8AllocationDeltaBarSegment = z.object({
  currentPercent: z.number().finite().min(0).optional(),
  label: z.string(),
  proposedPercent: z.number().finite().min(0).optional(),
  symbol: z.string().optional(),
  tone: z_ChatV8InsightWidgetTone.optional(),
});
export type T_ChatV8AllocationDeltaBarSegment = z.infer<
  typeof z_ChatV8AllocationDeltaBarSegment
>;

export const z_ChatV8AllocationDeltaBarVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    scenarioAmountUsd: z.number().finite().positive().optional(),
    segments: z.array(z_ChatV8AllocationDeltaBarSegment).min(1),
    targetSymbol: z.string(),
    visualType: z.literal("allocation_delta_bar"),
  }),
);
export type T_ChatV8AllocationDeltaBarVisual = z.infer<
  typeof z_ChatV8AllocationDeltaBarVisual
>;

export const z_ChatV8PortfolioReturnComparisonPoint = z.object({
  label: z.string(),
  returnPercent: z.number().finite().nullable(),
  scenarioReturnUsd: z.number().finite().nullable().optional(),
  tone: z_ChatV8InsightWidgetTone.optional(),
});
export type T_ChatV8PortfolioReturnComparisonPoint = z.infer<
  typeof z_ChatV8PortfolioReturnComparisonPoint
>;

export const z_ChatV8PortfolioReturnComparisonVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    basisAmountUsd: z.number().finite().positive().optional(),
    basisMode: z
      .union([z.literal("added_dollars"), z.literal("portfolio_basis")])
      .optional(),
    allocationSegments: z.array(z_ChatV8AllocationDeltaBarSegment).optional(),
    coveragePercent: z.number().finite().min(0).max(100).nullable(),
    currentMixAnnualVolatilityPercent: z
      .number()
      .finite()
      .nullable()
      .optional(),
    currentSharpeRatio: z.number().finite().nullable().optional(),
    current: z_ChatV8PortfolioReturnComparisonPoint,
    existingTargetWeightPercent: z.number().finite().nullable().optional(),
    maxSlotWeightPercent: z.number().finite().nullable().optional(),
    period: z.string(),
    recommendedSlotAmountUsd: z.number().finite().nullable().optional(),
    recommendedSlotWeightPercent: z.number().finite().nullable().optional(),
    recommendedTotalTargetWeightPercent: z
      .number()
      .finite()
      .nullable()
      .optional(),
    relativeEdgePercent: z.number().finite().nullable(),
    relativeEdgeUsd: z.number().finite().nullable().optional(),
    returnEdgePercent: z.number().finite().nullable().optional(),
    returnEdgeUsd: z.number().finite().nullable().optional(),
    riskDeltaAnnualVolatilityPercent: z.number().finite().nullable().optional(),
    scenarioAmountUsd: z.number().finite().positive().optional(),
    sharpeDelta: z.number().finite().nullable().optional(),
    slotMode: z.literal("frozen_current_mix_max_sharpe").optional(),
    target: z_ChatV8PortfolioReturnComparisonPoint,
    targetAnnualVolatilityPercent: z.number().finite().nullable().optional(),
    targetSharpeRatio: z.number().finite().nullable().optional(),
    targetSymbol: z.string(),
    visualType: z.literal("portfolio_return_comparison"),
  }),
);
export type T_ChatV8PortfolioReturnComparisonVisual = z.infer<
  typeof z_ChatV8PortfolioReturnComparisonVisual
>;

export const z_ChatV8EarningsSurpriseMetric = z.object({
  actual: z.number().finite().nullable(),
  estimated: z.number().finite().nullable(),
  label: z.string(),
  surprisePercent: z.number().finite().nullable(),
  tone: z_ChatV8InsightWidgetTone.optional(),
});
export type T_ChatV8EarningsSurpriseMetric = z.infer<
  typeof z_ChatV8EarningsSurpriseMetric
>;

export const z_ChatV8EarningsSurpriseReactionVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    metrics: z.array(z_ChatV8EarningsSurpriseMetric).min(1),
    priceReactionPercent: z.number().finite().nullable(),
    reportDate: z.string().nullable(),
    visualType: z.literal("earnings_surprise_reaction"),
  }),
);
export type T_ChatV8EarningsSurpriseReactionVisual = z.infer<
  typeof z_ChatV8EarningsSurpriseReactionVisual
>;

export const z_ChatV8InsiderFlowPoint = z.object({
  date: z.string(),
  direction: z.enum(["buy", "sell"]),
  label: z.string(),
  valueUsd: z.number().finite().min(0),
});
export type T_ChatV8InsiderFlowPoint = z.infer<typeof z_ChatV8InsiderFlowPoint>;

export const z_ChatV8InsiderFlowVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    buyValueUsd: z.number().finite().min(0),
    netValueUsd: z.number().finite(),
    sellValueUsd: z.number().finite().min(0),
    timeline: z.array(z_ChatV8InsiderFlowPoint),
    visualType: z.literal("insider_flow"),
  }),
);
export type T_ChatV8InsiderFlowVisual = z.infer<
  typeof z_ChatV8InsiderFlowVisual
>;

export const z_ChatV8PeerRangeMetric = z.object({
  higherIsBetter: z.boolean(),
  label: z.string(),
  max: z.number().finite().nullable(),
  median: z.number().finite().nullable(),
  min: z.number().finite().nullable(),
  target: z.number().finite().nullable(),
  unit: z.enum(["multiple", "percent"]),
});
export type T_ChatV8PeerRangeMetric = z.infer<typeof z_ChatV8PeerRangeMetric>;

export const z_ChatV8PeerRangePlotVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    metrics: z.array(z_ChatV8PeerRangeMetric).min(1),
    peerSymbols: z.array(z.string()),
    targetSymbol: z.string(),
    visualType: z.literal("peer_range_plot"),
  }),
);
export type T_ChatV8PeerRangePlotVisual = z.infer<
  typeof z_ChatV8PeerRangePlotVisual
>;

export const z_ChatV8EvidenceBalanceGroup = z.object({
  label: z.string(),
  tone: z_ChatV8InsightWidgetTone.optional(),
  totalWeight: z.number().finite().min(0),
  weight: z.number().finite().min(0),
});
export type T_ChatV8EvidenceBalanceGroup = z.infer<
  typeof z_ChatV8EvidenceBalanceGroup
>;

export const z_ChatV8EvidenceBalanceVisual = z.intersection(
  z_ChatV8InsightWidgetVisualBase,
  z.object({
    groups: z.array(z_ChatV8EvidenceBalanceGroup).min(1),
    thesis: z.string(),
    visualType: z.literal("evidence_balance"),
  }),
);
export type T_ChatV8EvidenceBalanceVisual = z.infer<
  typeof z_ChatV8EvidenceBalanceVisual
>;

export const z_ChatV8InsightWidgetVisual = z.union([
  z_ChatV8AllocationDeltaBarVisual,
  z_ChatV8PortfolioReturnComparisonVisual,
  z_ChatV8EarningsSurpriseReactionVisual,
  z_ChatV8InsiderFlowVisual,
  z_ChatV8PeerRangePlotVisual,
  z_ChatV8EvidenceBalanceVisual,
]);
export type T_ChatV8InsightWidgetVisual = z.infer<
  typeof z_ChatV8InsightWidgetVisual
>;

export const z_ChatV8InsightWidgetData = z.object({
  descriptor: z_ChatV8InsightWidgetDescriptor,
  insightKind: z_ChatV8InsightKind,
  symbol: z.string(),
  companyName: z.string().nullable(),
  title: z.string(),
  headline: z.string(),
  asOf: z.string(),
  dataQuality: z_ChatV8InsightWidgetDataQuality,
  missingSections: z.array(z.string()),
  metrics: z.array(z_ChatV8InsightWidgetMetric),
  sections: z.array(z_ChatV8InsightWidgetSection),
  visuals: z.array(z_ChatV8InsightWidgetVisual),
  peers: z.array(z_ChatV8InsightWidgetPeer).optional(),
  rawDebug: z_JsonValue.optional(),
});
export type T_ChatV8InsightWidgetData = z.infer<
  typeof z_ChatV8InsightWidgetData
>;

export const z_ChatV8TableWidget = z.object({
  title: z.string().optional(),
  columns: z
    .array(
      z.object({
        key: z.string(),
        label: z.string(),
      }),
    )
    .min(1),
  rows: z.array(z.record(z.string(), z_JsonValue)),
});
export type T_ChatV8TableWidget = z.infer<typeof z_ChatV8TableWidget>;

// Deterministic flow computation schemas. These are backend planning shapes,
// not Chat V8 event or widget contracts; flows project them into the canonical
// markdown + artifact response plan before persistence.

export const z_ChatV8PortfolioRatingBadgeTone =
  z_ChatV8InsightWidgetTone.exclude(["neutral"]);
export type T_ChatV8PortfolioRatingBadgeTone = z.infer<
  typeof z_ChatV8PortfolioRatingBadgeTone
>;

export const z_ChatV8PortfolioRatingInfoSheetKey = z.enum(["expected_return"]);
export type T_ChatV8PortfolioRatingInfoSheetKey = z.infer<
  typeof z_ChatV8PortfolioRatingInfoSheetKey
>;

export const z_ChatV8PortfolioRatingMetric = z.object({
  label: z.string().min(1),
  sublabel: z.string().min(1),
  badgeValue: z.string().min(1).max(8),
  badgeTone: z_ChatV8PortfolioRatingBadgeTone,
  infoSheetKey: z_ChatV8PortfolioRatingInfoSheetKey.optional(),
});
export type T_ChatV8PortfolioRatingMetric = z.infer<
  typeof z_ChatV8PortfolioRatingMetric
>;

export const z_ChatV8PortfolioRatingSuggestion = z.object({
  text: z.string().min(1),
  tickers: z.array(z_ChatV8TickerSymbol).max(12),
});
export type T_ChatV8PortfolioRatingSuggestion = z.infer<
  typeof z_ChatV8PortfolioRatingSuggestion
>;

export const z_ChatV8PortfolioRatingWidgetData = z.object({
  widgetType: z.literal("portfolio_rating"),
  verdict: z.string().min(1),
  metrics: z.array(z_ChatV8PortfolioRatingMetric).min(1),
  mainIssue: z.string().min(1),
  suggestion: z_ChatV8PortfolioRatingSuggestion,
  followUpPrompts: z.array(z.string().min(1)).max(6),
});
export type T_ChatV8PortfolioRatingWidgetData = z.infer<
  typeof z_ChatV8PortfolioRatingWidgetData
>;

// Renderers resolve tokens to theme colors; raw hexes never cross the wire.
// Shared by daily-recap items and company-analysis headers.
export const z_ChatV8DailyRecapIconBgToken = z.enum([
  "amber",
  "navy",
  "maroon",
  "emerald",
  "indigo",
]);
export type T_ChatV8DailyRecapIconBgToken = z.infer<
  typeof z_ChatV8DailyRecapIconBgToken
>;

// Renderers derive direction from the sign of percentChange/dollarChange and
// the icon letter from ticker.charAt(0).
export const z_ChatV8DailyRecapItem = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  ticker: z_ChatV8TickerSymbol,
  percentChange: z.number().finite(),
  dollarChange: z.number().finite(),
  iconBg: z_ChatV8DailyRecapIconBgToken,
  image: z.string().nullish(),
});
export type T_ChatV8DailyRecapItem = z.infer<typeof z_ChatV8DailyRecapItem>;

export const z_ChatV8DailyRecapWidgetData = z.object({
  widgetType: z.literal("daily_recap"),
  intro: z.string().min(1),
  items: z.array(z_ChatV8DailyRecapItem).min(1),
});
export type T_ChatV8DailyRecapWidgetData = z.infer<
  typeof z_ChatV8DailyRecapWidgetData
>;

export const z_ChatV8UpcomingEventStats = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("estimate"),
    prev: z.string().min(1),
    est: z.string().min(1),
  }),
  z.object({
    kind: z.literal("portfolio_weight"),
    portfolioPercent: z.number().finite().min(0).max(100),
  }),
]);
export type T_ChatV8UpcomingEventStats = z.infer<
  typeof z_ChatV8UpcomingEventStats
>;

// startsAt is the single date encoding per item; renderers derive the
// date-badge day/month from it. sourceEventId links the recurrence opt-in to
// the producing calendar/earnings source event for dedupe (RAI-1025).
export const z_ChatV8UpcomingEventAddToCalendar = z.object({
  eventName: z.string().min(1),
  startsAt: z.iso.datetime(),
  allDay: z.boolean().optional(),
  sourceEventId: z.string().min(1),
  offerRecurrenceOptIn: z.boolean(),
});
export type T_ChatV8UpcomingEventAddToCalendar = z.infer<
  typeof z_ChatV8UpcomingEventAddToCalendar
>;

export const z_ChatV8UpcomingEventItem = z.object({
  title: z.string().min(1),
  body: z.string().min(1),
  stats: z_ChatV8UpcomingEventStats,
  addToCalendar: z_ChatV8UpcomingEventAddToCalendar,
});
export type T_ChatV8UpcomingEventItem = z.infer<
  typeof z_ChatV8UpcomingEventItem
>;

export const z_ChatV8UpcomingEventsWidgetData = z.object({
  widgetType: z.literal("upcoming_events"),
  intro: z.string().min(1),
  items: z.array(z_ChatV8UpcomingEventItem).min(1),
});
export type T_ChatV8UpcomingEventsWidgetData = z.infer<
  typeof z_ChatV8UpcomingEventsWidgetData
>;

// ─── Table presentation upgrade (RAI-1092 / RAI-1097) ───────────────────────
// Progressive enhancement carried INSIDE a table artifact. Shipped clients'
// zod schemas strip unknown keys, so a table with `presentation` renders as
// the plain table on old builds and as the rich card on new ones — no new
// widget type on the wire, no capability gate, and immutable snapshots stay
// valid for every client version viewing the same thread.
//
// Contract (enforced by renderability-invariants + client parser tests):
// - CLOSED union. Every member must be a faithful re-presentation of data the
//   fallback table rows already carry; a widget without an honest table
//   fallback gets a real widget type instead, never a presentation.
// - Only deterministic flows attach it. The model-facing artifact schema
//   (z_ChatV8GenerateWidgetArtifactModelInput) must never accept it.
// - Renderers render EITHER the rich card OR the table, never both, and any
//   malformed presentation falls back to the table rather than unavailable.
export const z_ChatV8TablePresentation = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("portfolio_rating"),
    data: z_ChatV8PortfolioRatingWidgetData,
  }),
  z.object({
    kind: z.literal("upcoming_events"),
    data: z_ChatV8UpcomingEventsWidgetData,
  }),
]);
export type T_ChatV8TablePresentation = z.infer<
  typeof z_ChatV8TablePresentation
>;

export const z_ChatV8PresentableTableWidget = z_ChatV8TableWidget.extend({
  presentation: z_ChatV8TablePresentation.optional(),
});
export type T_ChatV8PresentableTableWidget = z.infer<
  typeof z_ChatV8PresentableTableWidget
>;

// ─── Company analysis deterministic flow (RAI-1036, design rev 2) ───────────
// Derivable presentation stays off the wire: change direction from the sign,
// icon letters from tickers, grade colors from the grade enum, and date badges
// from ISO datetimes. Body strings may carry markdown-lite **bold** markers.

export const z_ChatV8AnalysisSection = z.object({
  title: z.string().min(1),
  grade: z_ChatV8AnalysisGrade,
  body: z.string().min(1),
});
export type T_ChatV8AnalysisSection = z.infer<typeof z_ChatV8AnalysisSection>;

// value/note are deterministic server-side formatting (same precedent as the
// rating badgeValue) — the renderer never re-derives "$1.29T" or "25.9×".
export const z_ChatV8AnalysisStatCard = z.object({
  label: z.string().min(1),
  value: z.string().min(1).max(16),
  note: z.string().min(1),
});
export type T_ChatV8AnalysisStatCard = z.infer<typeof z_ChatV8AnalysisStatCard>;

// One inline compact series for the shipped interval, reusing the stock
// widget's columnar encoding (z_ChatV8StockWidgetColumnarInterval) rather than
// inventing a new one. Interval switching in the renderer loads other ranges
// through the existing stock-widget data path — scripted event payloads stay
// small and immutable.
export const z_ChatV8AnalysisPrice = z.object({
  current: z.number().finite(),
  changeLabel: z.string().min(1),
  interval: z_ChatV8StockWidgetInterval,
  series: z_ChatV8StockWidgetColumnarInterval,
});
export type T_ChatV8AnalysisPrice = z.infer<typeof z_ChatV8AnalysisPrice>;

// Raw dollar amounts (not billions); netMarginPct is percent (56 = 56%).
// Geometry, axis formatting, and bar/line scaling are the renderer's job.
export const z_ChatV8AnalysisIncomeRow = z.object({
  periodLabel: z.string().min(1).max(12),
  revenue: z.number().finite(),
  netIncome: z.number().finite(),
  netMarginPct: z.number().finite(),
});
export type T_ChatV8AnalysisIncomeRow = z.infer<
  typeof z_ChatV8AnalysisIncomeRow
>;

// actual is null for upcoming periods (expected-only marker in the design).
export const z_ChatV8AnalysisEpsRow = z.object({
  periodLabel: z.string().min(1).max(12),
  expected: z.number().finite(),
  actual: z.number().finite().nullable(),
});
export type T_ChatV8AnalysisEpsRow = z.infer<typeof z_ChatV8AnalysisEpsRow>;

export const z_ChatV8AnalysisPriceAlertAction = z.object({
  ticker: z_ChatV8TickerSymbol,
  targetPrice: z.number().finite().positive(),
  rationale: z.string().min(1),
});
export type T_ChatV8AnalysisPriceAlertAction = z.infer<
  typeof z_ChatV8AnalysisPriceAlertAction
>;

// The event title, start instant, and date badge all come from addToCalendar
// (eventName/startsAt) — deliberately not duplicated here despite the ticket's
// shorthand, per the no-derivable-fields rule.
export const z_ChatV8AnalysisEarningsEventAction = z.object({
  body: z.string().min(1),
  addToCalendar: z_ChatV8UpcomingEventAddToCalendar,
});
export type T_ChatV8AnalysisEarningsEventAction = z.infer<
  typeof z_ChatV8AnalysisEarningsEventAction
>;

// Offered when the analysis ran without any portfolio holdings — connecting
// a brokerage upgrades portfolio-fit from generic to holdings-aware
// (RAI-1057). The title/CTA are client copy; only the rationale ships.
export const z_ChatV8AnalysisConnectBrokerageAction = z.object({
  body: z.string().min(1),
});
export type T_ChatV8AnalysisConnectBrokerageAction = z.infer<
  typeof z_ChatV8AnalysisConnectBrokerageAction
>;

export const z_ChatV8CompanyAnalysisWidgetData = z
  .object({
    widgetType: z.literal("company_analysis"),
    groundingNote: z.string().min(1),
    company: z.object({
      name: z.string().min(1),
      ticker: z_ChatV8TickerSymbol,
      exchange: z.string().min(1),
      description: z.string().min(1),
      iconBg: z_ChatV8DailyRecapIconBgToken,
      image: z.string().nullish(),
    }),
    price: z_ChatV8AnalysisPrice,
    statCards: z.array(z_ChatV8AnalysisStatCard).min(1).max(4),
    take: z.object({
      headline: z.string().min(1),
      body: z.string().min(1),
    }),
    sections: z.array(z_ChatV8AnalysisSection).min(1).max(8),
    incomeTrends: z.object({
      quarterly: z.array(z_ChatV8AnalysisIncomeRow).min(1).max(12),
      yearly: z.array(z_ChatV8AnalysisIncomeRow).min(1).max(12),
    }),
    epsRows: z.array(z_ChatV8AnalysisEpsRow).min(1).max(12),
    portfolioFit: z.string().min(1),
    verdict: z.string().min(1),
    // RAI-1079: scrub vendor names from persisted analysis source lines too.
    sourcesLine: z.string().min(1).transform(scrubVendorMentions),
    followUpPrompts: z.array(z.string().min(1)).max(6),
    priceAlert: z_ChatV8AnalysisPriceAlertAction.optional(),
    earningsEvent: z_ChatV8AnalysisEarningsEventAction.optional(),
    connectBrokerage: z_ChatV8AnalysisConnectBrokerageAction.optional(),
  })
  .superRefine((data, ctx) => {
    // netMarginPct stays on the wire (sign/rounding would otherwise be
    // re-derived slightly differently by every consumer), so guard that it
    // agrees with the raw figures it summarizes.
    const checkRows = (rows: T_ChatV8AnalysisIncomeRow[], path: string) => {
      rows.forEach((row, index) => {
        if (row.revenue === 0) return;
        const derived = (row.netIncome / row.revenue) * 100;
        if (Math.abs(derived - row.netMarginPct) > 1) {
          ctx.addIssue({
            code: "custom",
            message: `netMarginPct ${row.netMarginPct} disagrees with netIncome/revenue (${derived.toFixed(1)})`,
            path: ["incomeTrends", path, index, "netMarginPct"],
          });
        }
      });
    };
    checkRows(data.incomeTrends.quarterly, "quarterly");
    checkRows(data.incomeTrends.yearly, "yearly");
  });
export type T_ChatV8CompanyAnalysisWidgetData = z.infer<
  typeof z_ChatV8CompanyAnalysisWidgetData
>;

// Deterministic holdings planning row. The flow projects these values into a
// canonical table artifact before persistence.
export const z_ChatV8HoldingsViewRow = z.object({
  symbol: z_ChatV8TickerSymbol,
  name: z.string().nullable(),
  units: z.number().finite().min(0),
  value: z.number().finite().min(0),
  weightPercent: z.number().finite().min(0).max(100),
  dayPercentChange: z.number().finite().nullable(),
  image: z.string().nullish(),
});
export type T_ChatV8HoldingsViewRow = z.infer<typeof z_ChatV8HoldingsViewRow>;

// Deterministic alert planning rows. The flow projects these into canonical
// table artifacts; edits still use conversational mutation tools.
export const z_ChatV8AlertsViewPriceAlert = z.object({
  ruleCuid: z.string().min(1),
  symbol: z_ChatV8TickerSymbol,
  kind: z.enum(["price_threshold", "price_change_pct"]),
  threshold: z.number().finite().nullable(),
  direction: z.string().nullable(),
  createdAt: z.string().nullable(),
});
export type T_ChatV8AlertsViewPriceAlert = z.infer<
  typeof z_ChatV8AlertsViewPriceAlert
>;

export const z_ChatV8AlertsViewEventAlert = z.object({
  cuid: z.string().min(1),
  triggerCondition: z.string().min(1),
  status: z.string().min(1),
  createdAt: z.string().nullable(),
});
export type T_ChatV8AlertsViewEventAlert = z.infer<
  typeof z_ChatV8AlertsViewEventAlert
>;

// Canonical rating-flow question set. The fieldCuids/optionCuids are the
// contract the backend flow keys answers on and the mobile renderer maps
// chips from; the JSON fixture is asserted equal to this constant so neither
// side hardcodes ids independently.
